import { Box, CircularProgress, Divider, MenuItem, Typography, makeStyles } from '@material-ui/core';
import ThreeDotMenu from './ThreeDotMenu';
import { useTranslation } from 'react-i18next';
import { ITheme, theme } from '../constants/theme';
import { textButtonHoverAndActive } from '../utils/styles/buttonStyles';
import { useHistory } from 'react-router-dom';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import CreditCard from '@material-ui/icons/CreditCard';
import { useEffect, useState } from 'react';
import { ProTeamSubscription, activateProSubscription, addProTeamDeliveryTranche, suspendProTeamSubscription } from "../redux/actions/subscriptions";
import { SubscriptionActivateModal } from "./SubscriptionActivateModal";
import { SubscriptionSuspendModal } from "./SubscriptionSuspendModal";
import { SubscriptionAddTrancheModal } from "./SubscriptionAddTrancheModal";

const useStyles = makeStyles((theme: ITheme) => ({
  card: {
    cursor: 'pointer',
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px 16px 24px 24px ",
    border: "1px solid rgba(0, 0, 0, 0.08)",
    boxSizing: "border-box",
    borderRadius: "20px",
    transition: "all 200ms ease",
    marginBottom: 16,
    ...textButtonHoverAndActive
  },
  component: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: 0,
    paddingRight: 0,
    marginTop: 24
  },
  active: {
    color: theme.palette.success.main,
    fontWeight: 700,
  },
  suspended: {
    color: theme.palette.warning.main,
    fontWeight: 700,
  },
  topTextContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginRight: 16
  },
  creditLeft: {
  },
  monthlyPrice: {
    margin: "8px 0",
    fontWeight: 700
  }
}));

enum SubscriptionCardModal {
  ADD_DELIVERY_TRANCHE,
  SUSPEND,
  ACTIVATE
}

interface ISubscriptionCardProps extends ProTeamSubscription {
  refreshList: () => void;
}


export default function SubscriptionCardPro(props: ISubscriptionCardProps) {
  const {
    ProTeamId,
    ProTeamName,
    HasActiveSubscription,
    HasCredits,
    SubscriptionPrice,
    SubscriptionNumberOfCredits,
    RemainingSubscriptionCredits,
    RemainingTrancheCredits,
    refreshList
  } = props;
  const history = useHistory<{ from: string; }>();
  const classes = useStyles();
  const { t } = useTranslation();
  const [visibleModal, setVisibleModal] = useState<SubscriptionCardModal>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(false);
  }, [props.HasActiveSubscription, props.HasCredits, props.RemainingTrancheCredits]);

  function onClick() {
    let url = `/admin/subscription/pro/${ProTeamId}`;
    history.push(url);
  }

  function onCloseModal() {
    setVisibleModal(undefined);
    refreshList();
  }

  const onActivate = (numberOfCredits: number, price: number) => {
    setLoading(true);
    activateProSubscription(
      { ProTeamId, NumberOfCredits: numberOfCredits, Price: Math.round(price * 100) },
      () => { onCloseModal(); },
      () => { }
    );
  };

  const onSuspend = () => {
    setLoading(true);
    suspendProTeamSubscription(
      { ProTeamId },
      () => { onCloseModal(); },
      () => { }
    );
  };

  const onAddDeliveryTranche = (numberOfCredits: number, price: number) => {
    setLoading(true);
    addProTeamDeliveryTranche(
      { ProTeamId, NumberOfCredits: numberOfCredits, Price: Math.round(price * 100) },
      () => { onCloseModal(); },
      () => { }
    );
  };

  return (
    <Box
      className={classes.card}>
      <Box
        className={classes.topTextContainer}
        onClick={onClick}>
        <Box>
          <Typography variant="h6" component="h2">
            {ProTeamName}
          </Typography>
          {HasActiveSubscription &&
            <>
              <Typography variant="body2" className={classes.monthlyPrice}>
{t('currencySymbol')}{SubscriptionPrice ? (SubscriptionPrice / 100)?.toFixed(2) : "-"}/month
              </Typography>
            </>
          }
          {HasActiveSubscription &&
            <Typography variant="body2" className={classes.creditLeft}>
              {SubscriptionNumberOfCredits || 0} credits/month
            </Typography>
          }
          {HasCredits &&
            <>
              <Divider style={{ margin: '8px 0' }} />
              <Typography variant="overline">Remaining</Typography>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {HasActiveSubscription &&
                  <Typography variant="body2" className={classes.creditLeft}>
                    <strong>Subscription:</strong> {RemainingSubscriptionCredits} credits
                  </Typography>
                }
                {HasActiveSubscription && RemainingTrancheCredits > 0 ? <Typography style={{ fontSize: 36 }}>·</Typography> : null}
                {RemainingTrancheCredits > 0 &&
                  <Typography variant="body2" className={classes.creditLeft}>
                    <strong>Tranche:</strong> {RemainingTrancheCredits} credits
                  </Typography>
                }
              </Box>
            </>
          }
        </Box>
        <Typography
          data-automation="promotion-code"
          variant="body2"
          className={HasActiveSubscription ? classes.active : classes.suspended}
          component="span">
          {loading ? <CircularProgress size={24} /> : HasActiveSubscription ? 'Active' : 'Inactive'}
        </Typography>
      </Box>
      <ThreeDotMenu>
        {HasActiveSubscription ?
          <MenuItem
            style={{ color: theme.palette.error.main }}
            onClick={() => setVisibleModal(SubscriptionCardModal.SUSPEND)}>
            <DeleteIcon />
            <Typography variant="body2" component="p" style={{ paddingLeft: '16px' }}>
              Suspend subscription
            </Typography>
          </MenuItem>
          :
          <MenuItem
            style={{ color: theme.palette.success.main }}
            onClick={() => setVisibleModal(SubscriptionCardModal.ACTIVATE)}>
            <CheckCircleIcon />
            <Typography variant="body2" component="p" style={{ paddingLeft: '16px' }}>
              Activate subscription
            </Typography>
          </MenuItem>
        }
        <MenuItem
          onClick={() => setVisibleModal(SubscriptionCardModal.ADD_DELIVERY_TRANCHE)}>
          <CreditCard />
          <Typography variant="body2" component="p" style={{ paddingLeft: '16px' }}>
            Add delivery tranche
          </Typography>
        </MenuItem>
      </ThreeDotMenu>

      {visibleModal !== undefined &&
        <>
          <SubscriptionActivateModal
            isVisible={visibleModal === SubscriptionCardModal.ACTIVATE}
            close={onCloseModal}
            activate={onActivate}
          />
          <SubscriptionSuspendModal
            isVisible={visibleModal === SubscriptionCardModal.SUSPEND}
            close={onCloseModal}
            suspend={onSuspend}
          />
          <SubscriptionAddTrancheModal
            // @ts-ignore
            isVisible={visibleModal === SubscriptionCardModal.ADD_DELIVERY_TRANCHE}
            close={onCloseModal}
            addTranche={onAddDeliveryTranche}
          />
        </>
      }
    </Box>
  );
}
